import { v4 } from 'uuid';
import { SelectOption } from '../../../../../../components/CiscoUi';
import { buildTrunkedNetworkInterface } from '../../../components/VirtualMachineEntities/Configure/Utils';
import { TEMP_UID_KEY } from '../constants';

export function createTemporaryNetwork(
  fields: AddNetworkFields,
  networks: Network[],
): HardwareNetworkInterface {
  const base = {
    networkInterface: {
      id: fields.interface,
    },
    uid: `${TEMP_UID_KEY}${v4()}`,
  };

  if (fields.trunked) {
    return {
      ...base,
      trunk: buildTrunkedNetworkInterface(
        networks,
        fields.trunkedNetworks ?? [],
      ),
    };
  }

  return {
    ...base,
    network: {
      _links: {},
      name: '',
      uid: fields.network!,
    },
    trunk: { enabled: false },
  };
}

export const convertNetworksToOptions = (
  networks: Network[],
): SelectOption<string>[] =>
  networks.map((item) => ({ name: item.name, value: item.uid }));

export const convertInterfacesToOptions = (
  interfaces: NetworkInterface[],
  selectedInterfaces: HardwareNetworkInterface[],
): SelectOption<string>[] => {
  return interfaces
    .filter((item) => {
      const selectedIds = selectedInterfaces.map(
        (item) => item.networkInterface.id,
      );

      return !selectedIds.includes(item.id);
    })
    .map((item) => ({ name: item.id, value: item.id }));
};

export const returnFieldOrUndefined = (
  field: UidOnlyField,
): UidOnlyField | undefined => {
  return field.uid ? field : undefined;
};

type NetworkReference = Pick<Network, 'name' | 'uid' | '_links'>;

export function buildNetworkNameByUid(
  networkRef: NetworkReference | undefined,
  networks: Network[],
  fallback = '',
): NetworkReference | undefined {
  if (!networkRef) {
    return undefined;
  }

  const catalogNetwork = networks.find(
    (network) => network.uid === networkRef.uid,
  );

  return {
    ...networkRef,
    name: catalogNetwork?.name ?? networkRef.name ?? fallback,
  };
}

export function buildTrunkNetworkNames(
  trunkNetworks: TrunkedNetworkInterface['networks'],
  networks: Network[],
): TrunkedNetworkInterface['networks'] {
  return trunkNetworks?.map(
    (trunkNetwork) =>
      buildNetworkNameByUid(trunkNetwork, networks, trunkNetwork.name)!,
  );
}

export function buildTrunkedNetworkInterfaceNames(
  item: HardwareNetworkInterface,
  networks: Network[],
): HardwareNetworkInterface {
  return {
    ...item,
    trunk: {
      ...item.trunk!,
      networks: buildTrunkNetworkNames(item.trunk?.networks, networks),
    },
  };
}

export function buildSingleNetworkInterfaceName(
  item: HardwareNetworkInterface,
  networks: Network[],
): HardwareNetworkInterface {
  return {
    ...item,
    network: buildNetworkNameByUid(
      item.network,
      networks,
      item.network?.name ?? '',
    )!,
  };
}

export function resolveNetworkInterfaceNames(
  item: HardwareNetworkInterface,
  networks: Network[],
): HardwareNetworkInterface {
  return item.trunk?.enabled
    ? buildTrunkedNetworkInterfaceNames(item, networks)
    : buildSingleNetworkInterfaceName(item, networks);
}

export function prepareHardwareNetworkInterfaceForSave(
  item: HardwareNetworkInterface,
): HardwareNetworkInterface | HardwareNetworkInterfaceToAdd {
  if (item.uid.includes(TEMP_UID_KEY)) {
    if (item.trunk?.enabled) {
      return {
        networkInterface: { id: item.networkInterface.id },
        trunk: item.trunk,
      };
    }

    return {
      network: { uid: item.network!.uid },
      networkInterface: { id: item.networkInterface.id },
    };
  }

  if (item.trunk?.enabled) {
    return {
      networkInterface: item.networkInterface,
      trunk: item.trunk,
      uid: item.uid,
    };
  }

  return item;
}
